import { useEffect, useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useShortcut } from "@/hooks/use-shortcut"
import { OPEN_SHORTCUTS_EVENT, shortcutsByGroup } from "@/lib/shortcuts"
import { Kbd } from "@/components/ui/kbd"

/**
 * The `?` cheatsheet, rendered straight from `lib/shortcuts.ts`.
 *
 * Before this the only shortcuts a user could discover were the three with a
 * `<kbd>` next to their button, plus a line in the table footer that is hidden
 * below `lg`. ⌘B and `[` were documented nowhere at all.
 *
 * It renders from the registry rather than from a hand-written list so it
 * cannot quietly go stale — a binding that is not in the registry does not
 * work, and one that is appears here.
 */
export function ShortcutsDialog() {
  const [open, setOpen] = useState(false)

useShortcut({ key: "?" }, () => setOpen((prev) => !prev), { bare: true })

  // Opened from the command palette. Deliberately not routed through
  // useShortcut: a programmatic open has no keyboard guards to satisfy,
  // and going through them is what broke this (INCO-357).
  useEffect(() => {
    const open = () => setOpen(true)
    window.addEventListener(OPEN_SHORTCUTS_EVENT, open)
    return () => window.removeEventListener(OPEN_SHORTCUTS_EVENT, open)
  }, [])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press <Key>?</Key> anywhere to bring this back.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {shortcutsByGroup().map(({ group, shortcuts }) => (
            <section key={group}>
              <h3 className="text-muted-foreground mb-2 text-xs font-semibold">
                {group}
              </h3>
              <dl className="flex flex-col">
                {shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.id}
                    className="flex h-8 items-center justify-between gap-4 border-b text-sm last:border-b-0"
                  >
                    <dt className="text-muted-foreground">{shortcut.label}</dt>
                    <dd className="flex shrink-0 items-center gap-1">
                      {shortcut.keys.map((key) => (
                        <Key key={key}>{key}</Key>
                      ))}
                      {shortcut.altKeys && (
                        <>
                          <span className="text-muted-foreground px-0.5 text-xs">or</span>
                          {shortcut.altKeys.map((key) => (
                            <Key key={key}>{key}</Key>
                          ))}
                        </>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Key({ children }: { children: React.ReactNode }) {
  return <Kbd>{children}</Kbd>
}
